import {
  Component,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  HostListener
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AgentService,
  AgentDetails,
  AgentStudy,
  UnassignedStudy,
  UpdateStudyRequest
} from '../services/agent.service';
import {
  QuestionnaireService,
  QuestionnaireScript,
  SaveQuestionnaireRequest,
  ResponseBranchData,
  StatementActionData,
  StartBuildRequest,
  BuildStatusResponse,
  PublishBuildRequest
} from '../services/questionnaire.service';
import { environment } from '../../environments/environment';
import { AuthService } from '../auth/auth.service';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormArray
} from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import Sortable from 'sortablejs';
import { interval, Subscription, forkJoin, of, Observable, firstValueFrom } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';


@Component({
  selector: 'app-agent-wizard',
  standalone: false,
  templateUrl: './agent-wizard.component.html',
  styleUrl: './agent-wizard.component.css'
})
export class AgentWizardComponent implements OnInit, OnDestroy {
  agentId: number | null = null;
  agentDetails: AgentDetails | null = null;
  isLoading = false;
  agentForm: FormGroup;
  studiesForm: FormGroup;
  isEditMode = false;
  currentStep = 1;

  // Pending calls banner
  hasPendingCallsWarning = false;

  // Agent status & publish confirmation modal
  agentStatus: string = 'DRAFT';
  showPublishConfirmModal = false;

  // Build and Publish state
  isBuilding = false;
  isPublishing = false;
  lastBuildVersion: number | null = null;
  lastBuildDate: Date | null = null;
  lastPublishVersion: number | null = null;
  lastPublishDate: Date | null = null;
  savedVersion: number | null = null; // Saved questionnaire version from CurrentQuestionnaire.Version
  nextBuildVersion = 1;
  nextPublishVersion = 1;

  // Build popup state
  showBuildPopup = false;
  buildPopupMessage = '';
  buildPopupVersion: number | null = null;
  isPublishPopup = false;

  // Build status polling
  private buildStatusSubscription?: Subscription;
  currentBuildStatus: BuildStatusResponse | null = null;
  private previousBuildStatus: string | null = null; // Track previous status to detect transitions
  isPollingBuildStatus = false;

  // Alert modal state
  showAlertModal = false;
  alertModalTitle = '';
  alertModalMessage = '';

  questionnaireScript: QuestionnaireScript[] = [];
  questionnaireInitialized = false;

  // Store unassigned studies from API
  private unassignedStudies: UnassignedStudy[] = [];
  private unassignedStudiesLoaded = false;

  studySearchText: string[] = [];
  studyDropdownOpen: boolean[] = [];

  // Store categories from API
  private categories: string[] = [];
  private categoriesLoaded = false;
  private categoriesLoading = false;
  private pendingStep3Navigation = false;

  // Track originally loaded studies to detect removals
  private originalAgentStudies: AgentStudy[] = [];

  private questionnaireHostRef?: ElementRef<HTMLDivElement>;
  private hostElement?: HTMLDivElement;
  private scriptContainer?: HTMLElement;
  private addTopLevelQuestionBtn?: HTMLButtonElement;
  private confirmationModal?: HTMLDivElement;
  private confirmationTitleEl?: HTMLElement;
  private confirmationMessageEl?: HTMLElement;
  private cancelDeleteBtn?: HTMLButtonElement;
  private confirmDeleteBtn?: HTMLButtonElement;
  private questionTemplate?: HTMLTemplateElement;
  private responseBranchTemplate?: HTMLTemplateElement;
  private statementTemplate?: HTMLTemplateElement;

  private confirmationCallback: (() => void) | null = null;
  private numericIdCounter = 1;

  private readonly handleAddQuestion = () => this.addTopLevelQuestion();
  private readonly handleScriptClick = (event: Event) =>
    this.onScriptContainerClick(event);
  private readonly handleCancelDelete = () => this.hideModal();
  private readonly handleConfirmDelete = () => {
    if (this.confirmationCallback) {
      this.confirmationCallback();
    }
    this.hideModal();
    this.confirmationCallback = null;
  };

  @ViewChild('questionnaireHost')
  set questionnaireHostSetter(value: ElementRef<HTMLDivElement> | undefined) {
    if (!value) {
      this.cleanupQuestionnaireBuilder();
      return;
    }
    this.questionnaireHostRef = value;
    setTimeout(() => this.initializeQuestionnaireBuilder(), 0);
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private agentService: AgentService,
    private questionnaireService: QuestionnaireService,
    private authService: AuthService,
    private fb: FormBuilder,
    private snackBar: MatSnackBar
  ) {
    this.agentForm = this.fb.group({
      name: ['', [Validators.required]],
      description: [''],
      status: ['DRAFT']
    });

    this.studiesForm = this.fb.group({
      studies: this.fb.array([])
    });
  }

  private getCurrentUser(): string {
    // Get current user from auth service - use email as identifier
    // Access the current value from the BehaviorSubject synchronously
    try {
      const userObservable = this.authService.user$();
      // BehaviorSubject allows direct access to current value
      const currentUser = (userObservable as any).source?.value || (userObservable as any).value;
      if (currentUser?.userEmail) {
        return currentUser.userEmail;
      }

      // Fallback: use subscribe pattern if direct access doesn't work
      let userEmail = 'unknown';
      const subscription = this.authService.user$().subscribe(user => {
        userEmail = user?.userEmail || 'unknown';
      });
      subscription.unsubscribe();
      return userEmail;
    } catch (error) {
      console.error('Error getting current user:', error);
      return 'unknown';
    }
  }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id && id !== 'new') {
      this.agentId = +id;
      this.isEditMode = true;
      this.loadAgentDetails();
      this.checkAgentPendingCallsOnLoad(+id);
    } else {
      this.isEditMode = false;
    }

    // Initialize confirmation modal elements (available on all steps)
    this.initializeConfirmationModal();

    // Load categories from API
    this.loadCategories();

    // Warn on unsaved changes
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
  }

  private initializeConfirmationModal(): void {
    // Initialize modal elements from document (available on all steps)
    this.confirmationModal = document.getElementById('confirmation-modal') as HTMLDivElement;
    this.confirmationTitleEl = document.getElementById('confirmation-title') as HTMLElement;
    this.confirmationMessageEl = document.getElementById('confirmation-message') as HTMLElement;
    this.cancelDeleteBtn = document.getElementById('cancel-delete-btn') as HTMLButtonElement;
    this.confirmDeleteBtn = document.getElementById('confirm-delete-btn') as HTMLButtonElement;

    // Add event listeners
    if (this.cancelDeleteBtn) {
      this.cancelDeleteBtn.addEventListener('click', this.handleCancelDelete);
    }
    if (this.confirmDeleteBtn) {
      this.confirmDeleteBtn.addEventListener('click', this.handleConfirmDelete);
    }
  }

  ngOnDestroy(): void {
    this.cleanupQuestionnaireBuilder();
    this.stopBuildStatusPolling();
    window.removeEventListener('beforeunload', this.beforeUnloadHandler);

    // Remove event listeners from confirmation modal
    if (this.cancelDeleteBtn) {
      this.cancelDeleteBtn.removeEventListener('click', this.handleCancelDelete);
    }
    if (this.confirmDeleteBtn) {
      this.confirmDeleteBtn.removeEventListener('click', this.handleConfirmDelete);
    }
  }

  beforeUnloadHandler = (event: BeforeUnloadEvent) => {
    if (this.hasUnsavedChanges()) {
      event.preventDefault();
      event.returnValue = '';
      return '';
    }
    return undefined;
  };

  hasUnsavedChanges(): boolean {
    return this.agentForm.dirty || this.studiesForm.dirty; // Add more if questionnaire/other sections apply
  }

  private checkAgentPendingCallsOnLoad(agentId: number): void {
    this.agentService.checkPendingCalls(agentId).subscribe({
      next: (res) => {
        this.hasPendingCallsWarning = res.hasPendingCalls;
      },
      error: () => {
        this.hasPendingCallsWarning = true;
      }
    });
  }

  loadAgentDetails(): void {
    if (!this.agentId) return;

    this.isLoading = true;
    this.agentService.getAgentById(this.agentId).subscribe({
      next: (agent) => {
        this.agentDetails = agent;
        this.agentStatus = agent.Status || 'DRAFT';
        this.agentForm.patchValue({
          name: agent.Name,
          description: agent.Description,
          status: agent.Status
        });

        // Store original studies for tracking removals
        this.originalAgentStudies = agent.AgentStudies ? [...agent.AgentStudies] : [];

        if (agent.AgentStudies && agent.AgentStudies.length > 0) {
          const studiesArray = this.studiesFormArray;
          studiesArray.clear();
          this.studySearchText = [];
          this.studyDropdownOpen = [];
          agent.AgentStudies.forEach(study => {
            studiesArray.push(this.createStudyFormGroup(study));
            this.studySearchText.push('');
            this.studyDropdownOpen.push(false);
          });
        }

        const questionnairePayload =
          (agent as any)?.CurrentQuestionnaire?.QuestionnaireData?.script;
        if (Array.isArray(questionnairePayload)) {
          this.questionnaireScript = questionnairePayload;
        } else {
          this.questionnaireScript = [];
        }

        // Set build and publish versions from agent details
        if (agent.PublishedBuild) {
          // Use PublishedBuild information - QuestionnaireVersion represents latest published version
          this.lastPublishVersion = agent.PublishedBuild.QuestionnaireVersion;
          this.nextPublishVersion = agent.PublishedBuild.QuestionnaireVersion + 1;

          // Use BuildVersion as the last build version for tracking
          // Note: The actual latest build (which might be in progress) will be loaded via loadCurrentBuildStatus
          this.lastBuildVersion = agent.PublishedBuild.BuildVersion;

          // If there's a published build, set it as current build status for display
          // This represents the latest published build, but we'll check for newer builds in loadCurrentBuildStatus
          this.currentBuildStatus = {
            Id: agent.PublishedBuild.Id,
            BuildVersion: agent.PublishedBuild.BuildVersion,
            Status: agent.PublishedBuild.Status,
            BuildStartedAt: agent.PublishedBuild.BuildStartedAt,
            BuildCompletedAt: agent.PublishedBuild.BuildCompletedAt,
            BuildArtifacts: null,
            BuildLog: agent.PublishedBuild.BuildLog
          };
          // Set previous status to prevent false transition detection
          this.previousBuildStatus = agent.PublishedBuild.Status;

          // Next build version should be based on PublishedBuild's BuildVersion + 1
          // But will be updated if a newer build exists when loadCurrentBuildStatus is called
          this.nextBuildVersion = agent.PublishedBuild.BuildVersion + 1;
        } else if (agent.PublishedBuildId) {
          // Fallback: If PublishedBuild object is not available but PublishedBuildId exists
          // Note: QuestionnaireVersion is not available in this case, so we use PublishedBuildId as approximation
          this.lastPublishVersion = agent.PublishedBuildId;
          this.nextPublishVersion = agent.PublishedBuildId + 1;
        }

        if (agent.CurrentQuestionnaireId) {
          // Current questionnaire version represents the saved version
          const currentQuestionnaire = agent.CurrentQuestionnaire;
          if (currentQuestionnaire?.Version) {
            this.savedVersion = currentQuestionnaire.Version;
            // Only use questionnaire version for build version if we don't have PublishedBuild info
            if (!agent.PublishedBuild) {
              this.lastBuildVersion = currentQuestionnaire.Version;
              this.nextBuildVersion = currentQuestionnaire.Version + 1;
            }
          }
        }

        if (this.questionnaireInitialized) {
          this.renderScriptData(this.questionnaireScript);
        }

        // Load current build status if available
        this.loadCurrentBuildStatus();

        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading agent details:', error);
        this.isLoading = false;
      }
    });
  }

  get studiesFormArray(): FormArray {
    return this.studiesForm.get('studies') as FormArray;
  }

  createStudyFormGroup(study?: any): FormGroup {
    const isEnabled = study?.Enabled ?? true;
    // Handle priority: normalize to "P1", "P2" format if needed
    let priorityValue = '';
    if (study?.Priority !== null && study?.Priority !== undefined && study?.Priority !== '') {
      const apiPriority = String(study.Priority).trim();
      // If priority is already in "P1" format, use it; otherwise normalize it
      priorityValue = apiPriority.startsWith('P')
        ? apiPriority
        : `P${apiPriority}`;
    }

    const group = this.fb.group({
      id: [study?.Id || null],
      study: [study?.Name || '', Validators.required],
      priority: [
        priorityValue,
        isEnabled ? [Validators.required] : []
      ],
      enabled: [isEnabled],
      description: [study?.Description || '']
    });

    group.get('enabled')!.valueChanges.subscribe((enabled: boolean) => {
      const priorityCtrl = group.get('priority');

      if (!enabled) {
        // Reset priority to empty string when disabling
        priorityCtrl?.setValue('', { emitEvent: false });
        priorityCtrl?.clearValidators();
        priorityCtrl?.disable({ emitEvent: false });
      } else {
        priorityCtrl?.setValidators(Validators.required);
        priorityCtrl?.enable({ emitEvent: false });
      }

      priorityCtrl?.updateValueAndValidity({ emitEvent: false });
    });

    // Initial state
    const priorityCtrl = group.get('priority');
    if (!isEnabled) {
      priorityCtrl?.disable({ emitEvent: false });
      priorityCtrl?.clearValidators();
    } else {
      // Ensure priority is enabled and has validators for enabled studies
      priorityCtrl?.enable({ emitEvent: false });
      priorityCtrl?.setValidators(Validators.required);
      // Set the normalized priority value explicitly
      if (priorityValue) {
        priorityCtrl?.setValue(priorityValue, { emitEvent: false });
      }
      priorityCtrl?.updateValueAndValidity({ emitEvent: false });
    }

    return group;
  }

  addStudy(): void {
    this.studiesFormArray.push(this.createStudyFormGroup());
    this.studySearchText.push('');
    this.studyDropdownOpen.push(false);
    // Refresh questionnaire status grids to include the new study
    if (this.questionnaireInitialized) {
      this.refreshAllStatusGrids();
    }
  }

  toggleStudyDropdown(index: number, event: MouseEvent): void {
    event.stopPropagation();
    const wasOpen = !!this.studyDropdownOpen[index];
    this.studyDropdownOpen = this.studyDropdownOpen.map(() => false);
    this.studyDropdownOpen[index] = !wasOpen;
    if (this.studyDropdownOpen[index]) {
      this.studySearchText[index] = '';
    }
  }

  selectStudy(index: number, studyName: string): void {
    const control = this.studiesFormArray.at(index);
    control.get('study')?.setValue(studyName);
    this.studyDropdownOpen[index] = false;
    this.studySearchText[index] = '';
    this.onStudyChange(index, { value: studyName });
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.studyDropdownOpen = this.studyDropdownOpen.map(() => false);
  }

  onStudyChange(index: number, event: Event | { value: string }): void {
    const selectedStudyName = (event as any).value !== undefined
      ? (event as { value: string }).value
      : ((event as Event).target as HTMLInputElement | HTMLSelectElement).value;
    const control = this.studiesFormArray.at(index);

    if (selectedStudyName) {
      // Try to find study in unassigned studies first
      const unassignedStudy = this.unassignedStudies.find(s => s.Name === selectedStudyName);
      if (unassignedStudy) {
        // Populate fields from unassigned study
        const enabled = unassignedStudy.Enabled;
        // Handle priority: normalize to "P1", "P2" format if needed
        let priorityValue = '';
        if (unassignedStudy.Priority !== null && unassignedStudy.Priority !== undefined && unassignedStudy.Priority !== '') {
          const apiPriority = String(unassignedStudy.Priority).trim();
          // If priority is already in "P1" format, use it; otherwise normalize it
          priorityValue = apiPriority.startsWith('P')
            ? apiPriority
            : `P${apiPriority}`;
        }

        // Handle priority validation based on enabled state first
        const priorityCtrl = control.get('priority');
        if (!enabled) {
          priorityCtrl?.clearValidators();
          priorityCtrl?.disable({ emitEvent: false });
        } else {
          priorityCtrl?.setValidators(Validators.required);
          priorityCtrl?.enable({ emitEvent: false });
        }

        // Set all values including priority
        control.patchValue({
          id: unassignedStudy.Id,
          priority: priorityValue,
          enabled: enabled,
          description: unassignedStudy.Description || ''
        }, { emitEvent: false });

        // Force update priority value and validation
        if (priorityValue) {
          priorityCtrl?.setValue(priorityValue, { emitEvent: false });
        }
        priorityCtrl?.updateValueAndValidity({ emitEvent: false });
      } else if (this.agentDetails?.AgentStudies) {
        // Try to find in agent's existing studies
        const agentStudy = this.agentDetails.AgentStudies.find(s => s.Name === selectedStudyName);
        if (agentStudy) {
          // Populate fields from agent study
          const enabled = agentStudy.Enabled;
          // Handle priority: normalize to "P1", "P2" format if needed
          let priorityValue = '';
          if (agentStudy.Priority !== null && agentStudy.Priority !== undefined && agentStudy.Priority !== '') {
            const apiPriority = String(agentStudy.Priority).trim();
            // If priority is already in "P1" format, use it; otherwise normalize it
            priorityValue = apiPriority.startsWith('P')
              ? apiPriority
              : `P${apiPriority}`;
          }

          // Handle priority validation based on enabled state first
          const priorityCtrl = control.get('priority');
          if (!enabled) {
            priorityCtrl?.clearValidators();
            priorityCtrl?.disable({ emitEvent: false });
          } else {
            priorityCtrl?.setValidators(Validators.required);
            priorityCtrl?.enable({ emitEvent: false });
          }

          // Set all values including priority
          control.patchValue({
            id: agentStudy.Id,
            priority: priorityValue,
            enabled: enabled,
            description: agentStudy.Description || ''
          }, { emitEvent: false });

          // Force update priority value and validation
          if (priorityValue) {
            priorityCtrl?.setValue(priorityValue, { emitEvent: false });
          }
          priorityCtrl?.updateValueAndValidity({ emitEvent: false });
        }
      }

      // Refresh questionnaire status grids when a study is selected
      if (this.questionnaireInitialized) {
        this.refreshAllStatusGrids();
      }
    } else {
      // Clear all fields if no study is selected
      control.patchValue({
        id: null,
        priority: '',
        enabled: true,
        description: ''
      }, { emitEvent: false });

      // Reset priority validation to required when clearing study
      const priorityCtrl = control.get('priority');
      priorityCtrl?.setValidators(Validators.required);
      priorityCtrl?.enable({ emitEvent: false });
      priorityCtrl?.updateValueAndValidity({ emitEvent: false });

      // Refresh questionnaire status grids when a study is cleared
      if (this.questionnaireInitialized) {
        this.refreshAllStatusGrids();
      }
    }
  }

  removeStudy(index: number): void {
    if (this.studiesFormArray.length === 0) {
      return;
    }

    const control = this.studiesFormArray.at(index);
    const studyName = control.get('study')?.value;

    if (!studyName) {
      // No study name, remove directly
      this.performRemoveStudy(index, studyName);
      return;
    }

    // Always show confirmation when removing a study with a name
    // This ensures users are warned about potentially losing status mappings in the Questionnaire
    const message = `Removing "${studyName}" will remove it from all Status Mappings in the Questionnaire. All status mappings for this study will be lost. Do you want to continue?`;

    // Use modal confirmation (available on all steps)
    if (this.confirmationModal && this.confirmationTitleEl && this.confirmationMessageEl) {
      this.showConfirmation(
        'Remove Study?',
        message,
        () => {
          this.performRemoveStudy(index, studyName);
        }
      );
    } else {
      // Fallback to browser confirm if modal not available
      const confirmed = confirm(message);
      if (confirmed) {
        this.performRemoveStudy(index, studyName);
      }
    }
  }

  private performRemoveStudy(index: number, studyName: string | null): void {
    // Get the control before removing it to get the study ID
    const control = this.studiesFormArray.at(index);
    const studyId = control.get('id')?.value;

    // Remove from questionnaire status grids first (before removing from form)
    if (this.questionnaireInitialized && studyName) {
      this.removeStudyFromStatusGrids(studyName);
    }

    // Remove from form
    this.studiesFormArray.removeAt(index);
    this.studySearchText.splice(index, 1);
    this.studyDropdownOpen.splice(index, 1);

    // Update AgentStudies in agentDetails if it exists
    if (this.agentDetails && this.agentDetails.AgentStudies) {
      if (studyId) {
        // Remove by ID if available
        this.agentDetails.AgentStudies = this.agentDetails.AgentStudies.filter(
          study => study.Id !== studyId
        );
      } else if (studyName) {
        // Remove by name as fallback
        this.agentDetails.AgentStudies = this.agentDetails.AgentStudies.filter(
          study => study.Name !== studyName
        );
      }
    }

    // Refresh all status grids to update available studies (after form removal)
    if (this.questionnaireInitialized) {
      // Use setTimeout to ensure DOM updates are complete
      setTimeout(() => {
        this.refreshAllStatusGrids();
      }, 0);
    }
  }

  private hasStudyStatusMappings(studyName: string): boolean {
    if (!this.questionnaireInitialized || !this.scriptContainer) {
      return false;
    }

    // Check all branch cards for status mappings containing this study
    // Show confirmation if study exists in any status grid (even with default values)
    const branchCards = this.scriptContainer.querySelectorAll('.response-branch-card');
    for (const branchCard of Array.from(branchCards)) {
      const checkbox = branchCard.querySelector('.status-mapping-toggle') as HTMLInputElement;
      // Check if status mapping section exists (regardless of checkbox state)
      // If the study is in the grid, we should show confirmation
      const statusGridItems = branchCard.querySelectorAll('.status-grid-item');
      for (const item of Array.from(statusGridItems)) {
        const itemElement = item as HTMLElement;
        // Check both dataset attribute formats (camelCase and kebab-case)
        const itemStudyName = itemElement.dataset['studyName'] || (itemElement.getAttribute('data-study-name'));
        if (itemStudyName === studyName) {
          // Study exists in status grid, show confirmation
          return true;
        }
      }
    }
    return false;
  }

  private removeStudyFromStatusGrids(studyName: string): void {
    if (!this.scriptContainer) return;

    // Remove study from all status grids
    const branchCards = this.scriptContainer.querySelectorAll('.response-branch-card');
    branchCards.forEach(branchCard => {
      // Find and remove status grid items for this study
      const statusGridItems = branchCard.querySelectorAll('.status-grid-item');
      statusGridItems.forEach(item => {
        const itemElement = item as HTMLElement;
        // Check both dataset attribute formats (camelCase and kebab-case)
        const itemStudyName = itemElement.dataset['studyName'] || (itemElement.getAttribute('data-study-name'));
        if (itemStudyName === studyName) {
          itemElement.remove();
        }
      });
    });
  }

  private refreshAllStatusGrids(): void {
    if (!this.questionnaireInitialized || !this.scriptContainer) {
      return;
    }

    // Get current studies from form (only valid studies with names)
    const currentStudies = new Set<string>();
    this.studiesFormArray.controls.forEach(control => {
      const studyName = control.get('study')?.value;
      if (studyName) {
        currentStudies.add(studyName);
      }
    });

    // Refresh status grids in all branch cards
    const branchCards = this.scriptContainer.querySelectorAll('.response-branch-card');
    branchCards.forEach(branchCard => {
      // Get existing status mappings before refreshing (only for studies that still exist)
      const existingMappings: Record<string, string> = {};
      const checkbox = branchCard.querySelector('.status-mapping-toggle') as HTMLInputElement;
      if (checkbox?.checked) {
        branchCard.querySelectorAll('.status-grid-item').forEach(item => {
          const itemElement = item as HTMLElement;
          const study = itemElement.dataset['studyName'] || itemElement.getAttribute('data-study-name');
          const select = item.querySelector('select') as HTMLSelectElement;
          // Only preserve mappings for studies that still exist in the form
          if (study && select && currentStudies.has(study)) {
            existingMappings[study] = select.value || 'GTS';
          }
        });
      }

      // Re-populate the status grid with current studies (this will remove studies not in currentStudies)
      this.populateStatusGrid(branchCard as HTMLElement, existingMappings);
    });
  }

  getAvailableStudies(index?: number): { name: string; id: number }[] {
    // If no index provided, return all studies from the form (for status grids)
    if (index === undefined) {
      const allStudiesMap = new Map<string, number>();

      // Get all studies from the form
      this.studiesFormArray.controls.forEach(control => {
        const studyName = control.get('study')?.value;
        const studyId = control.get('id')?.value;
        if (studyName) {
          allStudiesMap.set(studyName, studyId || 0);
        }
      });

      // Convert to sorted array
      return Array.from(allStudiesMap.entries())
        .map(([name, id]) => ({ name, id }))
        .sort((a, b) => a.name.localeCompare(b.name));
    }

    // Get studies currently selected in other form controls (excluding current index)
    const selectedFormStudies = new Set<string>();
    this.studiesFormArray.controls.forEach((control, i) => {
      if (i === index) {
        // Skip the current control - allow it to show its own selected value
        return;
      }
      const studyName = control.get('study')?.value;
      if (studyName) {
        selectedFormStudies.add(studyName);
      }
    });

    // Get the currently selected study for this index (if editing)
    let currentStudyName: string | null = null;
    if (this.studiesFormArray.at(index)) {
      currentStudyName = this.studiesFormArray.at(index).get('study')?.value || null;
    }

    // Get unassigned studies from API (excluding those already selected in other form controls)
    const availableStudiesMap = new Map<string, number>();

    this.unassignedStudies.forEach(study => {
      if (!selectedFormStudies.has(study.Name)) {
        availableStudiesMap.set(study.Name, study.Id);
      }
    });

    // Get studies already assigned to the current agent (from saved data)
    // Include them so users can see/edit existing assignments
    if (this.agentDetails && this.agentDetails.AgentStudies) {
      this.agentDetails.AgentStudies.forEach(study => {
        // Include if not selected in another form control, or if it's the current selection
        if (!selectedFormStudies.has(study.Name) || study.Name === currentStudyName) {
          // Use Id from AgentStudy
          availableStudiesMap.set(study.Name, study.Id);
        }
      });
    }

    // Always include the currently selected study for this control (if any)
    if (currentStudyName) {
      // Find the ID for the current study
      const currentStudy = this.unassignedStudies.find(s => s.Name === currentStudyName);
      if (currentStudy) {
        availableStudiesMap.set(currentStudyName, currentStudy.Id);
      } else if (this.agentDetails?.AgentStudies) {
        const agentStudy = this.agentDetails.AgentStudies.find(s => s.Name === currentStudyName);
        if (agentStudy) {
          availableStudiesMap.set(currentStudyName, agentStudy.Id);
        }
      }
    }

    // Convert to sorted array of objects with name and id
    let results = Array.from(availableStudiesMap.entries())
      .map(([name, id]) => ({ name, id }))
      .sort((a, b) => a.name.localeCompare(b.name));

    if (environment.production) {
      results = results.filter(s =>
        !/test/i.test(s.name) || s.name === currentStudyName
      );
    }

    const search = (this.studySearchText[index] || '').trim().toLowerCase();
    if (search) {
      results = results.filter(s =>
        s.name.toLowerCase().includes(search) || s.name === currentStudyName
      );
    }
    return results;
  }

  trackByStudyName(index: number, study: { name: string; id: number }): string {
    return study.name;
  }

  getAvailablePriorities(rowIndex: number): string[] {
    const total = this.studiesFormArray.length;
    // Currently selected priorities by other studies
    const selected: string[] = this.studiesFormArray.controls.map((ctrl, i) =>
      i !== rowIndex ? ctrl.get('priority')?.value : null
    ).filter(val => !!val);
    const all = Array.from({ length: total }, (_, i) => `P${i + 1}`);
    const current = this.studiesFormArray.at(rowIndex).get('priority')?.value;
    // Always include current
    return all.filter(p => !selected.includes(p) || p === current);
  }

  updateStudyIds(): void {
    // Update study IDs in form controls based on selected study names
    this.studiesFormArray.controls.forEach(control => {
      const studyName = control.get('study')?.value;
      if (studyName) {
        // Find study ID from unassigned studies
        const unassignedStudy = this.unassignedStudies.find(s => s.Name === studyName);
        if (unassignedStudy) {
          control.patchValue({ id: unassignedStudy.Id });
        } else if (this.agentDetails?.AgentStudies) {
          // Find from agent's existing studies
          const agentStudy = this.agentDetails.AgentStudies.find(s => s.Name === studyName);
          if (agentStudy) {
            control.patchValue({ id: agentStudy.Id });
          }
        }
      }
    });
  }

  saveStudies(): Observable<any> {
    if (!this.agentId || this.studiesFormArray.length === 0) {
      return of(null);
    }

    const userEmail = this.getCurrentUser();
    const updateRequests: Observable<any>[] = [];

    this.studiesFormArray.controls.forEach(control => {
      const id = control.get('id')?.value;
      const studyName = control.get('study')?.value;
      const priority = control.get('priority')?.value;
      const enabled = control.get('enabled')?.value;
      const description = control.get('description')?.value || '';

      // For enabled studies, priority is required. For disabled studies, priority is optional.
      if (id && studyName && this.agentId && (enabled ? priority : true)) {
        const payload: UpdateStudyRequest = {
          Enabled: enabled,
          AgentId: this.agentId,
          Priority: priority || null,
          Description: description,
          ModifiedBy: userEmail
        };

        updateRequests.push(
          this.agentService.updateStudy(id, payload).pipe(
            catchError(error => {
              console.error(`Error updating study ${id}:`, error);
              return of(null);
            })
          )
        );
      }
    });

    if (updateRequests.length === 0) {
      return of(null);
    }

    return forkJoin(updateRequests);
  }

  private loadUnassignedStudies(): void {
    if (this.unassignedStudiesLoaded) {
      return;
    }

    this.unassignedStudiesLoaded = true;
    this.unassignedStudies = [];

    // Fetch unassigned studies from API
    this.agentService.getUnassignedStudies().subscribe({
      next: (response) => {
        this.unassignedStudies = response.studies || [];
      },
      error: (error) => {
        console.error('Error loading unassigned studies:', error);
        this.unassignedStudiesLoaded = false; // Allow retry on error
      }
    });
  }

  onExit(): void {
    this.captureQuestionnaireSnapshot();
    this.router.navigate(['/agents-catalogs']);
  }

  onGoBack(): void {
    if (this.currentStep === 3) {
      this.captureQuestionnaireSnapshot();
    }
    if (this.currentStep > 1) {
      this.currentStep--;
      // Reload unassigned studies when going back to step 2
      if (this.currentStep === 2 && !this.unassignedStudiesLoaded) {
        this.loadUnassignedStudies();
      }
    }
  }

  onNext(): void {
    if (this.currentStep === 1) {
      if (this.agentForm.valid) {
        this.currentStep = 2;
        // Load unassigned studies when entering step 2
        if (!this.unassignedStudiesLoaded) {
          this.loadUnassignedStudies();
        }
      } else {
        this.agentForm.markAllAsTouched();
      }
    } else if (this.currentStep === 2) {
      const studiesArray = this.studiesFormArray;
      if (studiesArray.length === 0) {
        // At least one study is required
        this.showAlertMessage('Please add at least one study before proceeding.', 'Study Required');
        return;
      }

      let isValid = true;
      this.studiesFormArray.controls.forEach(control => {
        // Validate all studies - study name is always required
        // For enabled studies, priority is also required (handled by form validators)
        // For disabled studies, priority is optional (validators are cleared)
        const enabled = control.get('enabled')?.value;
        const priority = control.get('priority')?.value;

        // Explicitly check: if study is enabled, priority must not be null, undefined, or empty
        if (enabled && (priority === null || priority === undefined || priority === '')) {
          isValid = false;
          control.get('priority')?.markAsTouched();
        }

        if (control.invalid) {
          isValid = false;
          control.markAllAsTouched();
        }
      });

      if (isValid) {
        // Update study IDs based on selected study names
        this.updateStudyIds();

        // Ensure categories are loaded before entering questionnaire step
        if (!this.categoriesLoaded) {
          this.pendingStep3Navigation = true;
          this.isLoading = true;
          this.loadCategories();
          return;
        }

        this.currentStep = 3;
      }
    }
  }

  isStudiesFormValid(): boolean {
    const studiesArray = this.studiesFormArray;
    // At least one study is required
    if (studiesArray.length === 0) {
      return false;
    }
    // Validate all studies
    return studiesArray.controls.every(control => {
      const studyName = control.get('study')?.value;
      const enabled = control.get('enabled')?.value;
      const priority = control.get('priority')?.value;

      // If study name is selected and study is enabled, priority must not be null, undefined, or empty
      if (studyName && enabled && (priority === null || priority === undefined || priority === '')) {
        return false;
      }

      // For disabled studies, priority validators are cleared, so control.valid will be true
      // if study name is provided. For enabled studies, both study name and priority are required.
      return control.valid;
    });
  }

  addQuestionFromToolbar(): void {
    if (this.questionnaireInitialized) {
      this.addTopLevelQuestion();
    }
  }

  onPreviewQuestions(): void {
    if (!this.questionnaireInitialized) {
      return;
    }
    const payload = this.serializeQuestionnaire();
    console.log('Preview Questionnaire Payload:', payload);
  }

  private saveQuestionnaire(): void {
    if (!this.questionnaireInitialized || !this.agentId) {
      console.error('Cannot save questionnaire: questionnaire not initialized or no agent selected');
      return;
    }

    this.isLoading = true;
    const script = this.serializeQuestionnaire();
    const userEmail = this.getCurrentUser();
    const currentDate = new Date().toISOString();

    // Get current study IDs from form
    const currentStudyIds = new Set<number>();
    this.studiesFormArray.controls.forEach(control => {
      const id = control.get('id')?.value;
      if (id && id > 0) {
        currentStudyIds.add(id);
      }
    });

    // Find removed studies (studies that were originally assigned but are no longer in the form)
    // For removed studies, only include Id and AgentId: null
    // Use originalAgentStudies instead of agentDetails.AgentStudies since removed studies
    // are filtered out from agentDetails.AgentStudies
    const removedStudies: any[] = [];
    if (this.originalAgentStudies && this.originalAgentStudies.length > 0) {
      this.originalAgentStudies.forEach(originalStudy => {
        if (originalStudy.Id > 0 && !currentStudyIds.has(originalStudy.Id)) {
          // This study was removed - only include Id and AgentId: null
          removedStudies.push({
            Id: originalStudy.Id,
            AgentId: null
          });
        }
      });
    }

    // Build agent_studies array from form
    const agentStudies = this.studiesFormArray.controls.map(control => {
      const id = control.get('id')?.value || 0;
      const studyName = control.get('study')?.value || '';
      const enabled = control.get('enabled')?.value ?? true;
      const priorityValue = control.get('priority')?.value;
      // For disabled studies, priority should be null/empty. For enabled studies, use the value or empty string.
      const priority = enabled ? (priorityValue || '') : (priorityValue || null);
      const description = control.get('description')?.value || '';

      // Find StudyId from unassigned studies or agent studies
      let studyId = 0;
      let existingStudy: AgentStudy | undefined;

      const unassignedStudy = this.unassignedStudies.find(s => s.Name === studyName);
      if (unassignedStudy) {
        studyId = unassignedStudy.StudyId;
      } else if (this.agentDetails?.AgentStudies) {
        existingStudy = this.agentDetails.AgentStudies.find(s => s.Name === studyName);
        if (existingStudy) {
          studyId = existingStudy.StudyId;
        }
      }

      // For existing studies (id > 0), preserve original CreatedBy and CreatedAt
      // For new studies (id === 0), use current user and date
      const isExisting = id > 0 && existingStudy;
      const createdBy = isExisting && existingStudy ? (existingStudy.CreatedBy || userEmail) : userEmail;
      const createdAt = isExisting && existingStudy ? (existingStudy.CreatedAt || currentDate) : currentDate;

      return {
        Id: id,
        Name: studyName,
        AgentId: this.agentId!,
        StudyId: studyId,
        Priority: priority,
        Enabled: enabled,
        Description: description,
        CreatedBy: createdBy,
        CreatedAt: createdAt,
        ModifiedBy: userEmail,
        ModifiedAt: currentDate
      };
    });

    // Combine current studies with removed studies
    const allAgentStudies = [...agentStudies, ...removedStudies];

    // Get agent details from form
    const agentName = this.agentForm.get('name')?.value || '';
    const agentDescription = this.agentForm.get('description')?.value || '';

    const payload: SaveQuestionnaireRequest = {
      agent_id: this.agentId,
      questionnaire_data: {
        script: script
      },
      agent_studies: allAgentStudies,
      agent_details: {
        name: agentName,
        desc: agentDescription
      },
      created_by: userEmail
    };

    this.questionnaireService.saveQuestionnaire(payload).subscribe({
      next: (response) => {
        console.log('Questionnaire saved successfully:', response);
        this.questionnaireScript = script;
        // Update saved version from response
        if (response.version) {
          this.savedVersion = response.version;
        }
        this.isLoading = false;
        // Show success toast
        this.snackBar.open('Questionnaire saved successfully!', 'Close', {
          duration: 3000,
          horizontalPosition: 'end',
          verticalPosition: 'top',
          panelClass: ['success-toast']
        });
      },
      error: (error) => {
        console.error('Error saving questionnaire:', error);
        this.isLoading = false;
      }
    });
  }

  onSaveQuestionnaire(): void {
    if (!this.questionnaireInitialized) {
      return;
    }

    if (!this.validateQuestions()) {
      return;
    }

    this.saveQuestionnaire();
  }

  private validateQuestions(): boolean {
    if (!this.scriptContainer) {
      return true; // No questions to validate
    }

    // Clear all existing invalid indicators first
    this.clearAllValidationErrors();

    const questionCards = Array.from(this.scriptContainer.children)
      .filter(child => (child as HTMLElement).classList.contains('question-card')) as HTMLElement[];

    if (questionCards.length === 0) {
      return true; // No questions to validate
    }

    const invalidQuestions: string[] = [];

    questionCards.forEach((card, index) => {
      const questionText = card.querySelector('.question-text') as HTMLTextAreaElement;
      const categorySelect = card.querySelector('.question-category') as HTMLSelectElement;
      const questionId = card.dataset['questionId'] || `Question ${index + 1}`;

      const textValue = questionText?.value?.trim() || '';
      const categoryValue = categorySelect?.value?.trim() || '';

      if (!textValue || !categoryValue) {
        invalidQuestions.push(questionId);

        // Add visual indicators for invalid fields
        if (!textValue && questionText) {
          questionText.classList.add('is-invalid');
          // Scroll to the first invalid question
          if (invalidQuestions.length === 1) {
            card.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
        if (!categoryValue && categorySelect) {
          categorySelect.classList.add('is-invalid');
        }
      }
    });

    if (invalidQuestions.length > 0) {
      const questionList = invalidQuestions.join(', ');
      this.showAlertMessage(
        `Please fill in the category and question text for: ${questionList}. Both fields are required.`,
        'Validation Error'
      );
      return false;
    }

    return true;
  }

  private clearAllValidationErrors(): void {
    if (!this.scriptContainer) {
      return;
    }

    // Clear invalid indicators from all question fields
    const questionTexts = this.scriptContainer.querySelectorAll('.question-text.is-invalid');
    questionTexts.forEach(text => text.classList.remove('is-invalid'));

    const categorySelects = this.scriptContainer.querySelectorAll('.question-category.is-invalid');
    categorySelects.forEach(select => select.classList.remove('is-invalid'));
  }

  private attachQuestionValidationListeners(questionCard: HTMLElement): void {
    const questionText = questionCard.querySelector('.question-text') as HTMLTextAreaElement;
    const categorySelect = questionCard.querySelector('.question-category') as HTMLSelectElement;

    // Attach event listeners to clear validation errors when fields are filled
    if (questionText && !(questionText as any).__validationListenerAttached) {
      questionText.addEventListener('input', () => {
        const textValue = questionText.value?.trim() || '';
        if (textValue) {
          questionText.classList.remove('is-invalid');
        }
      });
      (questionText as any).__validationListenerAttached = true;
    }

    if (categorySelect && !(categorySelect as any).__validationListenerAttached) {
      categorySelect.addEventListener('change', () => {
        const categoryValue = categorySelect.value?.trim() || '';
        if (categoryValue) {
          categorySelect.classList.remove('is-invalid');
        }
      });
      (categorySelect as any).__validationListenerAttached = true;
    }
  }

  onBuild(): void {
    if (!this.agentId || this.isBuilding || this.isPollingBuildStatus) {
      return;
    }

    // Check if there's already a build in progress
    if (this.currentBuildStatus &&
      (this.currentBuildStatus.Status === 'PENDING' || this.currentBuildStatus.Status === 'IN_PROGRESS')) {
      this.showAlertMessage('A build is already in progress. Please wait for it to complete.');
      return;
    }

    this.isBuilding = true;
    const userEmail = this.getCurrentUser();

    const payload: StartBuildRequest = {
      agent_id: this.agentId,
      created_by: userEmail
    };

    console.log('Starting build with payload:', payload);

    this.questionnaireService.startBuild(payload).subscribe({
      next: (response) => {
        console.log('Build started successfully:', response);
        this.lastBuildVersion = response.build_version;
        this.lastBuildDate = new Date();
        this.nextBuildVersion = response.build_version + 1;
        this.isBuilding = false;
        // Reset previous status tracking for new build
        this.previousBuildStatus = null;
        // Clear current build status to avoid showing old build status in modal
        this.currentBuildStatus = null;
        // Show success popup
        this.buildPopupVersion = response.build_version;
        this.buildPopupMessage = `Build ${response.build_version} started successfully. Build runs in background (5-30 minutes).`;
        this.isPublishPopup = false;
        this.showBuildPopup = true;
        // Start polling for build status
        this.startBuildStatusPolling();
      },
      error: (error) => {
        console.error('Error starting build:', error);
        this.isBuilding = false;

        // Extract error message from API response
        // API returns: { "detail": "error message" }
        let errorMessage = 'Error starting build. Please try again.';
        if (error?.error?.detail) {
          errorMessage = error.error.detail;
        } else if (error?.error?.message) {
          errorMessage = error.error.message;
        } else if (typeof error?.error === 'string') {
          // Handle case where error.error is a string
          errorMessage = error.error;
        } else if (error?.message) {
          errorMessage = error.message;
        }

        this.buildPopupMessage = errorMessage;
        this.buildPopupVersion = null;
        this.isPublishPopup = false;
        this.showBuildPopup = true;
      }
    });
  }

  onPublish(): void {
    if (!this.agentId || this.isPublishing) {
      return;
    }

    // Check if there's a completed build to publish
    if (!this.currentBuildStatus || this.currentBuildStatus.Status !== 'COMPLETED') {
      this.showAlertMessage('Please wait for the build to complete before publishing.');
      return;
    }

    // First publish: show confirmation modal
    if (this.agentStatus === 'DRAFT') {
      this.showPublishConfirmModal = true;
      return;
    }

    this.executePublish();
  }

  private executePublish(): void {
    this.isPublishing = true;
    const userEmail = this.getCurrentUser();

    const payload: PublishBuildRequest = {
      modified_by: userEmail
    };

    this.questionnaireService.publishBuild(this.agentId!, payload).subscribe({
      next: (response) => {
        this.agentStatus = 'PUBLISHED';
        this.lastPublishVersion = response.published_build_version;
        this.lastPublishDate = new Date();
        this.nextPublishVersion = response.published_build_version + 1;
        this.isPublishing = false;
        this.buildPopupVersion = response.published_build_version;
        this.buildPopupMessage = `Build ${response.published_build_version} published successfully!`;
        this.isPublishPopup = true;
        this.showBuildPopup = true;
      },
      error: (error) => {
        this.isPublishing = false;

        // Extract error message from API response
        let errorMessage = 'Error publishing build. Please try again.';
        if (error?.error?.detail) {
          errorMessage = error.error.detail;
        } else if (error?.error?.message) {
          errorMessage = error.error.message;
        } else if (error?.message) {
          errorMessage = error.message;
        }


        this.buildPopupMessage = errorMessage;
        this.buildPopupVersion = null;
        this.isPublishPopup = false;
        this.showBuildPopup = true;
      }
    });
  }

  onPublishConfirmModalPublish(): void {
    this.showPublishConfirmModal = false;
    this.executePublish();
  }

  onPublishConfirmModalKeepDraft(): void {
    this.showPublishConfirmModal = false;
  }

  closeBuildPopup(): void {
    this.showBuildPopup = false;
    this.buildPopupMessage = '';
    this.buildPopupVersion = null;
    this.isPublishPopup = false;
  }

  startBuildStatusPolling(): void {
    if (!this.agentId || this.isPollingBuildStatus) {
      return;
    }

    this.isPollingBuildStatus = true;

    // Get initial status immediately, then poll every 10 seconds
    this.questionnaireService.getLatestBuildStatus(this.agentId).subscribe({
      next: (status) => {
        this.updateBuildStatus(status);
      },
      error: (error) => {
        console.error('Error getting initial build status:', error);
      }
    });

    // Poll every 10 seconds
    this.buildStatusSubscription = interval(10000)
      .pipe(
        switchMap(() => this.questionnaireService.getLatestBuildStatus(this.agentId!))
      )
      .subscribe({
        next: (status) => {
          this.updateBuildStatus(status);

          // Stop polling if build is completed or failed AND it's the build we're tracking
          if ((status.Status === 'COMPLETED' || status.Status === 'FAILED') &&
            (!this.buildPopupVersion || status.BuildVersion === this.buildPopupVersion)) {
            this.stopBuildStatusPolling();
          }
        },
        error: (error) => {
          console.error('Error polling build status:', error);
          // Don't stop polling on error, just log it
        }
      });
  }

  private updateBuildStatus(status: BuildStatusResponse): void {
    const previousStatus = this.previousBuildStatus;

    // Only update currentBuildStatus if this status is for the build we're tracking
    // If we have a buildPopupVersion, only accept status updates that match that version
    if (this.buildPopupVersion && status.BuildVersion !== this.buildPopupVersion) {
      // This status is for a different build, ignore it
      console.log(`Ignoring status update for build ${status.BuildVersion}, tracking build ${this.buildPopupVersion}`);
      return;
    }

    this.currentBuildStatus = status;
    console.log('Build status update:', status);

    // Update popup message if still open
    if (this.showBuildPopup && this.buildPopupVersion) {
      // Only show completion modal if status transitioned TO completed (not if it was already completed)
      const justCompleted = status.Status === 'COMPLETED' &&
        previousStatus !== null &&
        previousStatus !== 'COMPLETED' &&
        (previousStatus === 'PENDING' || previousStatus === 'IN_PROGRESS');

      if (justCompleted) {
        // Build just completed - show completion modal
        this.buildPopupMessage = `Build ${this.buildPopupVersion} completed successfully!`;
      } else if (status.Status === 'FAILED') {
        // Only update to failed if it transitioned to failed
        const justFailed = previousStatus !== null &&
          previousStatus !== 'FAILED' &&
          (previousStatus === 'PENDING' || previousStatus === 'IN_PROGRESS');
        if (justFailed) {
          this.buildPopupMessage = `Build ${this.buildPopupVersion} failed. Please check the logs and try again.`;
          this.buildPopupVersion = null; // Show error icon
        }
      } else if (status.Status === 'PENDING' || status.Status === 'IN_PROGRESS') {
        // For pending/in-progress, update the message
        let statusText = status.Status === 'PENDING' ? 'pending' : 'in progress';
        this.buildPopupMessage = `Build ${this.buildPopupVersion} is ${statusText}. Build runs in background (5-30 minutes).`;
      }
      // If status is already COMPLETED when popup opens (not a transition), don't change the message
    }

    // Update previous status for next check
    this.previousBuildStatus = status.Status;
  }

  stopBuildStatusPolling(): void {
    if (this.buildStatusSubscription) {
      this.buildStatusSubscription.unsubscribe();
      this.buildStatusSubscription = undefined;
      this.isPollingBuildStatus = false;
    }
  }

  loadCurrentBuildStatus(): void {
    if (!this.agentId) {
      return;
    }

    // Get current build status (this gets the latest build, which might be newer than PublishedBuild)
    this.questionnaireService.getLatestBuildStatus(this.agentId).subscribe({
      next: (status) => {
        // Only update if this build is newer than the PublishedBuild (if exists)
        // or if we don't have a PublishedBuild
        if (!this.agentDetails?.PublishedBuild ||
          status.BuildVersion >= this.agentDetails.PublishedBuild.BuildVersion) {
          this.currentBuildStatus = status;
          // Update next build version based on latest build
          if (status.BuildVersion >= (this.lastBuildVersion || 0)) {
            this.lastBuildVersion = status.BuildVersion;
            this.nextBuildVersion = status.BuildVersion + 1;
          }
        }
        // Initialize previous status to current status so we can detect transitions
        this.previousBuildStatus = this.currentBuildStatus?.Status || null;
        // If build is in progress, start polling
        if (status.Status === 'PENDING' || status.Status === 'IN_PROGRESS') {
          this.startBuildStatusPolling();
        }
      },
      error: (error) => {
        // If there's no build yet, that's fine - just log it
        console.log('No active build found or error loading build status:', error);
      }
    });
  }

  private initializeQuestionnaireBuilder(): void {
    if (this.questionnaireInitialized || !this.questionnaireHostRef) {
      return;
    }

    this.hostElement = this.questionnaireHostRef.nativeElement;
    this.scriptContainer = this.hostElement.querySelector('#script-container') as HTMLElement;
    this.addTopLevelQuestionBtn = this.hostElement.querySelector('#addTopLevelQuestionBtn') as HTMLButtonElement;

    // Modal elements are initialized in ngOnInit, but ensure they're set
    // (They should already be initialized from document.getElementById in ngOnInit)
    if (!this.confirmationModal) {
      this.confirmationModal = document.getElementById('confirmation-modal') as HTMLDivElement;
    }
    if (!this.confirmationTitleEl) {
      this.confirmationTitleEl = document.getElementById('confirmation-title') as HTMLElement;
    }
    if (!this.confirmationMessageEl) {
      this.confirmationMessageEl = document.getElementById('confirmation-message') as HTMLElement;
    }
    if (!this.cancelDeleteBtn) {
      this.cancelDeleteBtn = document.getElementById('cancel-delete-btn') as HTMLButtonElement;
    }
    if (!this.confirmDeleteBtn) {
      this.confirmDeleteBtn = document.getElementById('confirm-delete-btn') as HTMLButtonElement;
    }
    this.questionTemplate = this.hostElement.querySelector('#question-template') as HTMLTemplateElement;
    this.responseBranchTemplate = this.hostElement.querySelector('#response-branch-template') as HTMLTemplateElement;
    this.statementTemplate = this.hostElement.querySelector('#statement-template') as HTMLTemplateElement;

    if (!this.scriptContainer || !this.questionTemplate || !this.responseBranchTemplate || !this.statementTemplate) {
      return;
    }

    this.addTopLevelQuestionBtn?.addEventListener('click', this.handleAddQuestion);
    this.scriptContainer.addEventListener('click', this.handleScriptClick);
    // Modal button listeners are already set up in initializeConfirmationModal()

    if (this.questionnaireScript.length) {
      this.renderScriptData(this.questionnaireScript);
    } else {
      this.addTopLevelQuestion();
    }

    this.initSortable(this.scriptContainer);
    this.questionnaireInitialized = true;
  }

  private cleanupQuestionnaireBuilder(): void {
    if (!this.hostElement) {
      return;
    }

    if (this.questionnaireInitialized) {
      this.captureQuestionnaireSnapshot();
    }

    this.addTopLevelQuestionBtn?.removeEventListener('click', this.handleAddQuestion);
    this.scriptContainer?.removeEventListener('click', this.handleScriptClick);
    // Don't remove modal listeners here - they're needed on all steps and cleaned up in ngOnDestroy

    this.hostElement = undefined;
    this.scriptContainer = undefined;
    this.addTopLevelQuestionBtn = undefined;
    // Don't clear modal elements here - they're available on all steps
    this.questionTemplate = undefined;
    this.responseBranchTemplate = undefined;
    this.statementTemplate = undefined;
    this.questionnaireInitialized = false;
  }

  private captureQuestionnaireSnapshot(): void {
    if (!this.questionnaireInitialized || !this.scriptContainer) {
      return;
    }
    this.questionnaireScript = this.serializeQuestionnaire();
  }

  private initSortable(container?: HTMLElement | null): void {
    if (!container || !container.dataset['sortableGroup']) return;

    const existing = (container as any)._sortableInstance as Sortable | undefined;
    if (existing) {
      existing.destroy();
    }

    (container as any)._sortableInstance = new Sortable(container, {
      group: container.dataset['sortableGroup'],
      handle: '.handle',
      animation: 150,
      ghostClass: 'draggable-ghost',
      onEnd: () => this.updateAllQuestionIDs()
    });
  }

  private renderScriptData(data: QuestionnaireScript[]): void {
    if (!this.scriptContainer) return;

    this.scriptContainer.innerHTML = '';
    if (!data || data.length === 0) {
      this.addTopLevelQuestion(undefined, true);
    } else {
      data.forEach(question => this.addTopLevelQuestion(question, true));
    }
    this.updateAllQuestionIDs();
  }

  private addTopLevelQuestion(data?: QuestionnaireScript, skipIdUpdate = false): HTMLElement | null {
    if (!this.scriptContainer) return null;
    return this.addQuestionCardToContainer(this.scriptContainer, data, false, skipIdUpdate);
  }

  private addNestedQuestion(
    container?: HTMLElement | null,
    data?: QuestionnaireScript,
    skipIdUpdate = false
  ): HTMLElement | null {
    if (!container) return null;
    return this.addQuestionCardToContainer(container, data, true, skipIdUpdate);
  }

  private addQuestionCardToContainer(
    container: HTMLElement,
    data?: QuestionnaireScript,
    isNested = false,
    skipIdUpdate = false
  ): HTMLElement {
    if (!this.questionTemplate) throw new Error('Question template not found');

    const fragment = this.questionTemplate.content.cloneNode(true) as DocumentFragment;
    const questionCard = fragment.querySelector('.question-card') as HTMLElement;
    if (isNested) {
      questionCard.classList.add('nested-question-card');
    }

    const categorySelect = questionCard.querySelector('.question-category') as HTMLSelectElement;
    this.populateCategoryDropdown(categorySelect);

    // Add event listeners to clear validation errors when fields are filled
    this.attachQuestionValidationListeners(questionCard);

    const branchContainer = questionCard.querySelector('.response-branch-container') as HTMLElement;
    this.initSortable(branchContainer);

    container.appendChild(questionCard);

    if (data) {
      this.applyQuestionData(questionCard, data);
    }

    if (!skipIdUpdate) {
      this.updateAllQuestionIDs();
    }

    return questionCard;
  }

  private applyQuestionData(questionCard: HTMLElement, data: QuestionnaireScript): void {
    const titleInput = questionCard.querySelector('.question-title-input') as HTMLInputElement;
    const questionText = questionCard.querySelector('.question-text') as HTMLTextAreaElement;
    const noteInput = questionCard.querySelector('.question-note') as HTMLTextAreaElement;
    const categorySelect = questionCard.querySelector('.question-category') as HTMLSelectElement;
    const branchContainer = questionCard.querySelector('.response-branch-container') as HTMLElement;

    // Add event listeners to clear validation errors when fields are filled
    this.attachQuestionValidationListeners(questionCard);

    titleInput.value = data.text ?? '';
    questionText.value = data.text ?? '';
    noteInput.value = data.note ?? '';

    if (data.category) {
      if (!this.categoriesLoaded && !Array.from(categorySelect.options).some(opt => opt.value === data.category)) {
        categorySelect.add(new Option(data.category, data.category));
      }
      categorySelect.value = data.category;
    }

    branchContainer.innerHTML = '';
    data.responseBranches?.forEach(branch =>
      this.addResponseBranch(branchContainer, branch, true)
    );
  }

  private addResponseBranch(
    container?: HTMLElement | null,
    data?: ResponseBranchData,
    skipIdUpdate = false
  ): HTMLElement | null {
    if (!container || !this.responseBranchTemplate) return null;

    const fragment = this.responseBranchTemplate.content.cloneNode(true) as DocumentFragment;
    const branchCard = fragment.querySelector('.response-branch-card') as HTMLElement;

    container.appendChild(branchCard);
    this.populateStatusGrid(branchCard, data?.statusMappings ?? undefined);

    const actionsContainer = branchCard.querySelector('.follow-up-actions-container') as HTMLElement;
    this.initSortable(actionsContainer);

    if (data) {
      this.applyBranchData(branchCard, data);
    }

    if (!skipIdUpdate) {
      this.updateAllQuestionIDs();
    }

    return branchCard;
  }

  private applyBranchData(branchCard: HTMLElement, data: ResponseBranchData): void {
    const conditionInput = branchCard.querySelector('.branch-condition') as HTMLInputElement;
    conditionInput.value = data.condition ?? '';

    const hasStatuses = data.statusMappings !== null;
    this.applyStatusToggle(branchCard, hasStatuses);

    if (data.statusMappings) {
      Array.from(branchCard.querySelectorAll('.status-grid-item')).forEach(item => {
        const study = (item as HTMLElement).dataset['studyName'];
        const select = item.querySelector('select') as HTMLSelectElement;
        if (study && select) {
          const value = data.statusMappings?.[study];
          if (value) {
            if (!Array.from(select.options).some(opt => opt.value === value)) {
              select.add(new Option(value, value));
            }
            select.value = value;
          }
        }
      });
    }

    const actionsContainer = branchCard.querySelector('.follow-up-actions-container') as HTMLElement;
    Array.from(actionsContainer.querySelectorAll('[data-type]')).forEach(action => action.remove());

    if (data.actions?.length) {
      data.actions.forEach(action => {
        if (this.isQuestionAction(action)) {
          this.addNestedQuestion(actionsContainer, action as QuestionnaireScript, true);
        } else {
          this.addStatement(actionsContainer, action as StatementActionData);
        }
      });
    }
  }

  private addStatement(container: HTMLElement, data?: StatementActionData): HTMLElement {
    if (!this.statementTemplate) throw new Error('Statement template not found');
    const fragment = this.statementTemplate.content.cloneNode(true) as DocumentFragment;
    const statementCard = fragment.querySelector('.statement-card') as HTMLElement;
    container.appendChild(statementCard);

    const typeSelect = statementCard.querySelector('.statement-type') as HTMLSelectElement;
    const conditionInput = statementCard.querySelector('.statement-condition') as HTMLInputElement;
    const textArea = statementCard.querySelector('.statement-text') as HTMLTextAreaElement;

    if (data) {
      if (data.type && !Array.from(typeSelect.options).some(opt => opt.value === data.type)) {
        typeSelect.add(new Option(data.type, data.type));
      }
      typeSelect.value = data.type ?? 'statement';
      conditionInput.value = data.condition ?? '';
      textArea.value = data.text ?? '';
    }

    return statementCard;
  }

  private isQuestionAction(action: QuestionnaireScript | StatementActionData): action is QuestionnaireScript {
    return (action as QuestionnaireScript).responseBranches !== undefined;
  }

  private loadCategories(): void {
    if (this.categoriesLoaded || this.categoriesLoading) {
      return;
    }

    this.categoriesLoading = true;
    this.questionnaireService.getCategories().subscribe({
      next: (response) => {
        if (response.success && response.categories && Array.isArray(response.categories)) {
          // API can return either string[] or { Id, Name }[]
          this.categories = response.categories
            .map((c: any) => (typeof c === 'string' ? c : c?.Name))
            .filter((name: any): name is string => typeof name === 'string' && name.trim().length > 0);
          this.categoriesLoaded = true;
          console.log('Categories loaded:', this.categories);
          // Refresh all category dropdowns if questionnaire is already initialized
          if (this.questionnaireInitialized && this.scriptContainer) {
            this.refreshAllCategoryDropdowns(true);
          }

          if (this.pendingStep3Navigation) {
            this.pendingStep3Navigation = false;
            this.currentStep = 3;
          }
        } else {
          console.warn('Invalid categories response:', response);
          this.categoriesLoaded = false;
          this.showAlertMessage('Unable to load categories. Please try again.', 'Category Load Error');
          this.pendingStep3Navigation = false;
        }
        this.categoriesLoading = false;
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading categories:', error);
        this.categoriesLoaded = false;
        this.categoriesLoading = false;
        this.isLoading = false;
        this.pendingStep3Navigation = false;
        this.showAlertMessage('Failed to load categories from server. Please try again.', 'Category Load Error');
      }
    });
  }

  private refreshAllCategoryDropdowns(strictValidation = false): void {
    if (!this.scriptContainer) return;

    let removedInvalidSelections = 0;
    const categorySelects = this.scriptContainer.querySelectorAll('.question-category');
    categorySelects.forEach((select) => {
      const wasRemoved = this.populateCategoryDropdown(select as HTMLSelectElement, strictValidation);
      if (wasRemoved) {
        removedInvalidSelections++;
      }
    });

    if (strictValidation && removedInvalidSelections > 0) {
      const message =
        removedInvalidSelections === 1
          ? '1 invalid category was removed because it no longer exists in the database.'
          : `${removedInvalidSelections} invalid categories were removed because they no longer exist in the database.`;
      this.showAlertMessage(message, 'Invalid Categories Removed');
    }
  }

  private populateCategoryDropdown(select: HTMLSelectElement, strictValidation = false): boolean {
    // Save the current value to restore it after repopulating
    const currentValue = select.value;

    select.innerHTML = '<option value="">Select a Category *</option>';

    // Use categories from API if loaded, otherwise ensure they're being loaded
    let categoriesToUse = this.categories;
    if (categoriesToUse.length === 0 && !this.categoriesLoaded && !this.categoriesLoading) {
      // If categories aren't loaded yet, trigger loading
      this.loadCategories();
      // Use empty array for now, will be refreshed when categories load
      categoriesToUse = [];
    }

    categoriesToUse.forEach(cat => {
      const opt = document.createElement('option');
      opt.value = cat;
      opt.textContent = cat;
      select.appendChild(opt);
    });

    // Restore the previous selection only if it exists in DB categories
    if (currentValue && categoriesToUse.includes(currentValue)) {
      select.value = currentValue;
      return false;
    }

    // In strict validation mode, report that an invalid selection was cleared
    return strictValidation && !!currentValue;
  }

  private populateStatusGrid(branchCard: HTMLElement, existing?: Record<string, string> | null): void {
    const grid = branchCard.querySelector('.status-grid') as HTMLElement;
    if (!grid) return;

    grid.innerHTML = '';

    const hint = document.createElement('div');
    hint.className = 'status-grid-hint';
    hint.textContent = "Select a status for each study. Default is 'GTS'.";
    hint.style.marginBottom = '4px';
    hint.style.fontSize = '12px';
    hint.style.color = '#8f9bb3';
    grid.appendChild(hint);

    const availableStudies = this.getAvailableStudies(); // Get all available studies for status mapping
    // Use only studies that are currently in the form - don't re-add removed studies
    const studies = availableStudies.map(s => s.name);
    // Use only the current available studies, don't add back removed ones

    studies.forEach(study => {
      const item = document.createElement('div');
      item.className = 'status-grid-item';
      item.dataset['studyName'] = study;
      // Add inline styles to ensure flexbox works
      item.style.display = 'flex';
      item.style.alignItems = 'center';
      item.style.justifyContent = 'space-between';
      item.style.gap = '12px';
      item.style.padding = '4px 0';
      item.style.marginBottom = '4px';
      item.style.width = '100%';
      item.style.boxSizing = 'border-box';

      const label = document.createElement('label');
      label.textContent = `${study}:`;
      label.title = study;
      // Add inline styles to the label
      label.style.flex = '0 0 220px';
      label.style.fontSize = '14px';
      label.style.color = '#2e384d';

      const select = document.createElement('select');
      select.className = 'study-status-input';
      // Add inline styles to the select
      select.style.flex = '1';
      // select.style.minWidth = '200px';
      select.style.maxWidth = '400px';
      select.style.padding = '10px 12px';
      select.style.borderRadius = '5px';
      select.style.border = '1px solid #d9def1';
      select.style.backgroundColor = '#ffffff';

      console.log('Created status grid item:', item, 'with classes:', item.className);
      const statuses = ['GTS', 'DNQ', 'TD', 'PD'];

      statuses.forEach(status => {
        const opt = document.createElement('option');
        opt.value = status;
        opt.textContent = status;
        select.appendChild(opt);
      });

      const value = existing?.[study];
      if (value) {
        if (!statuses.includes(value)) {
          const customOpt = document.createElement('option');
          customOpt.value = value;
          customOpt.textContent = value;
          select.appendChild(customOpt);
        }
        select.value = value;
      } else {
        select.value = 'GTS';
      }

      item.appendChild(label);
      item.appendChild(select);
      grid.appendChild(item);
    });
  }

  private setToggleState(button: HTMLButtonElement, on: boolean): void {
    if (on) {
      button.classList.remove('off');
    } else {
      button.classList.add('off');
    }
  }

  private applyStatusToggle(branchCard: HTMLElement, enable: boolean): void {
    const checkbox = branchCard.querySelector('.status-mapping-toggle') as HTMLInputElement;
    const statusSection = branchCard.querySelector('.status-mapping-section') as HTMLElement;
    const toggleButton = branchCard.querySelector('.status-toggle-button') as HTMLButtonElement;

    if (checkbox) checkbox.checked = enable;
    if (statusSection) statusSection.style.display = enable ? '' : 'none';
    if (toggleButton) this.setToggleState(toggleButton, enable);

    if (!enable) {
      branchCard.querySelectorAll('.study-status-input').forEach(select => {
        (select as HTMLSelectElement).value = 'GTS';
      });
    }
  }

  private updateAllQuestionIDs(): void {
    if (!this.scriptContainer) return;

    this.numericIdCounter = 1;
    const topLevelQuestions = Array.from(this.scriptContainer.children)
      .filter(child => (child as HTMLElement).classList.contains('question-card')) as HTMLElement[];

    topLevelQuestions.forEach((questionEl, index) => {
      const newId = `q${index + 1}`;
      this.updateQuestionIDRecursive(questionEl, newId);
    });
  }

  private updateQuestionIDRecursive(questionEl: HTMLElement, newId: string): void {
    questionEl.dataset['questionId'] = newId;
    questionEl.dataset['numericId'] = String(this.numericIdCounter++);

    const idDisplay = questionEl.querySelector('.question-id-display');
    if (idDisplay) {
      idDisplay.textContent = newId.toUpperCase();
    }

    const branchContainer = questionEl.querySelector('.response-branch-container');
    if (!branchContainer) return;

    const childQuestions: HTMLElement[] = [];
    Array.from(branchContainer.children)
      .filter(child => (child as HTMLElement).classList.contains('response-branch-card'))
      .forEach(branchEl => {
        const actionsContainer = (branchEl as HTMLElement).querySelector('.follow-up-actions-container');
        if (actionsContainer) {
          Array.from(actionsContainer.children).forEach(actionEl => {
            if ((actionEl as HTMLElement).classList?.contains('question-card')) {
              childQuestions.push(actionEl as HTMLElement);
            }
          });
        }
      });

    childQuestions.forEach((childEl, index) => {
      const suffix = String.fromCharCode(97 + index);
      this.updateQuestionIDRecursive(childEl, `${newId}${suffix}`);
    });
  }

  private showConfirmation(title: string, message: string, callback: () => void): void {
    if (!this.confirmationModal || !this.confirmationTitleEl || !this.confirmationMessageEl) return;
    this.confirmationTitleEl.textContent = title;
    this.confirmationMessageEl.textContent = message;
    this.confirmationCallback = callback;
    this.confirmationModal.classList.add('active');
  }

  private hideModal(): void {
    this.confirmationModal?.classList.remove('active');
  }

  private onScriptContainerClick(event: Event): void {
    const target = event.target as HTMLElement;
    const button = target.closest('button');
    if (!button) return;

    if (button.classList.contains('collapse-btn') && button.closest('.question-card')) {
      button.closest('.question-card')?.classList.toggle('collapsed');
    } else if (button.classList.contains('collapse-branch-btn')) {
      button.closest('.response-branch-card')?.classList.toggle('branch-collapsed');
    } else if (button.classList.contains('delete-item-btn')) {
      const item = button.closest('[data-type]') as HTMLElement | null;
      if (!item) return;
      const typeLabel =
        item.dataset['type'] === 'question'
          ? 'Question'
          : item.dataset['type'] === 'branch'
            ? 'Response Branch'
            : 'Statement';

      this.showConfirmation(
        `Delete ${typeLabel}?`,
        'This will remove the item and all its contents.',
        () => {
          item.remove();
          this.updateAllQuestionIDs();
        }
      );
    } else if (button.classList.contains('add-response-branch-btn')) {
      const questionCard = button.closest('.question-card');
      const branchContainer = questionCard?.querySelector('.response-branch-container') as HTMLElement;
      this.addResponseBranch(branchContainer);
    } else if (button.classList.contains('add-action-question-btn')) {
      const branchCard = button.closest('.response-branch-card');
      const actionsContainer = branchCard?.querySelector('.follow-up-actions-container') as HTMLElement;
      this.addNestedQuestion(actionsContainer);
    } else if (button.classList.contains('add-action-statement-btn')) {
      const branchCard = button.closest('.response-branch-card');
      const actionsContainer = branchCard?.querySelector('.follow-up-actions-container') as HTMLElement;
      if (actionsContainer) {
        this.addStatement(actionsContainer);
      }
    } else if (button.classList.contains('status-toggle-button')) {
      const branchCard = button.closest('.response-branch-card') as HTMLElement;
      const checkbox = branchCard?.querySelector('.status-mapping-toggle') as HTMLInputElement;
      if (!checkbox) return;

      if (checkbox.checked) {
        this.showConfirmation(
          'Disable Status Mapping?',
          "This will hide the status mappings for this condition. Any custom statuses set will be reset to default ('GTS') if you re-enable it. Continue?",
          () => this.applyStatusToggle(branchCard, false)
        );
      } else {
        this.applyStatusToggle(branchCard, true);
      }
    }
  }

  private serializeQuestionnaire(): QuestionnaireScript[] {
    if (!this.scriptContainer) {
      return this.questionnaireScript;
    }

    const questionCards = Array.from(this.scriptContainer.children)
      .filter(child => (child as HTMLElement).classList.contains('question-card')) as HTMLElement[];

    return questionCards.map(card => this.serializeQuestionCard(card));
  }

  private serializeQuestionCard(questionEl: HTMLElement): QuestionnaireScript {
    const questionText = questionEl.querySelector('.question-text') as HTMLTextAreaElement;
    const noteInput = questionEl.querySelector('.question-note') as HTMLTextAreaElement;
    const categorySelect = questionEl.querySelector('.question-category') as HTMLSelectElement;

    return {
      numericId: Number(questionEl.dataset['numericId'] ?? 0),
      questionID: questionEl.dataset['questionId'] ?? '',
      text: questionText?.value?.trim() ?? '',
      category: categorySelect?.value ?? '',
      note: noteInput?.value?.trim() || null,
      responseBranches: this.serializeBranches(questionEl)
    };
  }

  private serializeBranches(questionEl: HTMLElement): ResponseBranchData[] {
    const branchContainer = questionEl.querySelector('.response-branch-container');
    if (!branchContainer) return [];

    const branchCards = Array.from(branchContainer.children)
      .filter(child => (child as HTMLElement).classList.contains('response-branch-card')) as HTMLElement[];

    return branchCards.map(branch => this.serializeBranch(branch));
  }

  private serializeBranch(branchCard: HTMLElement): ResponseBranchData {
    const conditionInput = branchCard.querySelector('.branch-condition') as HTMLInputElement;
    const checkbox = branchCard.querySelector('.status-mapping-toggle') as HTMLInputElement;

    let statusMappings: Record<string, string> | null = null;
    if (checkbox?.checked) {
      statusMappings = {};
      branchCard.querySelectorAll('.status-grid-item').forEach(item => {
        const study = (item as HTMLElement).dataset['studyName'];
        const select = item.querySelector('select') as HTMLSelectElement;
        if (study) {
          statusMappings![study] = select?.value ?? 'GTS';
        }
      });
    }

    const actions = this.serializeActions(branchCard);

    return {
      condition: conditionInput?.value?.trim() ?? '',
      statusMappings,
      actions
    };
  }

  private serializeActions(
    branchCard: HTMLElement
  ): (QuestionnaireScript | StatementActionData)[] | null {
    const actionsContainer = branchCard.querySelector('.follow-up-actions-container');
    if (!actionsContainer) return null;

    const actionNodes = Array.from(actionsContainer.children)
      .filter(child => (child as HTMLElement).dataset['type']) as HTMLElement[];

    const actions = actionNodes.map(actionEl => {
      if (actionEl.dataset['type'] === 'question') {
        return this.serializeQuestionCard(actionEl);
      }
      return this.serializeStatement(actionEl);
    });

    return actions.length ? actions : null;
  }

  private serializeStatement(statementEl: HTMLElement): StatementActionData {
    const typeSelect = statementEl.querySelector('.statement-type') as HTMLSelectElement;
    const conditionInput = statementEl.querySelector('.statement-condition') as HTMLInputElement;
    const textArea = statementEl.querySelector('.statement-text') as HTMLTextAreaElement;

    return {
      type: (typeSelect?.value as StatementActionData['type']) ?? 'statement',
      condition: conditionInput?.value?.trim() || undefined,
      text: textArea?.value?.trim() ?? ''
    };
  }

  collapseAllQuestions(): void {
    if (!this.scriptContainer) return;
    const cards = this.scriptContainer.querySelectorAll('.question-card');
    cards.forEach(card => card.classList.add('collapsed'));
  }

  showAlertMessage(message: string, title: string = 'Alert'): void {
    this.alertModalTitle = title;
    this.alertModalMessage = message;
    this.showAlertModal = true;
  }

  closeAlertModal(): void {
    this.showAlertModal = false;
    this.alertModalTitle = '';
    this.alertModalMessage = '';
  }
}
